import React, { useState, useEffect, useCallback } from "react";
import { BASE_URL, API_ENDPOINTS } from "../config/apiconfig";
import { toast } from "react-toastify";
import { FiX, FiFileText, FiDownload, FiPackage, FiArrowLeft } from "react-icons/fi";
import "../style/PassbookDownloadModal.css";

const PassbookDownloadModal = ({ isOpen, onClose, customerId, customerName }) => {
  const [showProductSelection, setShowProductSelection] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.PASSBOOK.GET_SUMMARY(customerId)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.status}`);
      }

      const data = await response.json();
      setProducts(data || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // Fetch products when product selection is shown
  useEffect(() => {
    if (showProductSelection && customerId) {
      fetchProducts();
    }
  }, [showProductSelection, customerId, fetchProducts]);

  const handleDownloadComplete = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.PASSBOOK.DOWNLOAD_COMPLETE(customerId)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to download PDF: ${response.status}`);
      }

      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = `complete-passbook-${customerName}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(fileUrl);
      }, 100);

      toast.success("Complete passbook downloaded successfully");
      onClose();
    } catch (error) {
      console.error('Error downloading complete passbook:', error);
      toast.error("Failed to download complete passbook");
    }
  };

  const handleDownloadProduct = async (productName) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}${API_ENDPOINTS.PASSBOOK.DOWNLOAD_PRODUCT(customerId, productName)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to download PDF: ${response.status}`);
      }

      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = `passbook-${productName}-${customerName}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(fileUrl);
      }, 100);

      toast.success(`Passbook for ${productName} downloaded successfully`);
      onClose();
    } catch (error) {
      console.error('Error downloading product passbook:', error);
      toast.error("Failed to download product passbook");
    }
  };

  const handleProductSelection = () => {
    setShowProductSelection(true);
  };

  const handleBack = () => {
    setShowProductSelection(false);
  };

  if (!isOpen) return null;

  return (
    <div 
      onClick={onClose}
      className="cv-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="cv-modal"
      >
        {!showProductSelection ? (
          // Main options
          <>
            <div className="cv-modal-header">
              <div className="cv-header-main">
                <div className="cv-header-title">
                  <div className="cv-header-icon">
                    <FiFileText />
                  </div>
                  <h3>Passbook PDF</h3>
                </div>
                <p>Download passbook for {customerName}</p>
              </div>
              <button
                onClick={onClose}
                className="cv-modal-close"
                aria-label="Close"
              >
                <FiX />
              </button>
            </div>

            <div className="cv-modal-body">
              <div className="cv-info-grid">
                <button
                  onClick={handleDownloadComplete}
                  className="cv-option-card"
                >
                  <div className="cv-option-icon">
                    <FiFileText />
                  </div>
                  <div className="cv-option-content">
                    <h4>Complete Passbook</h4>
                    <p>Download full passbook with all products</p>
                  </div>
                  <div className="cv-option-action">
                    <FiDownload />
                  </div>
                </button>

                <button
                  onClick={handleProductSelection}
                  className="cv-option-card"
                >
                  <div className="cv-option-icon">
                    <FiPackage />
                  </div>
                  <div className="cv-option-content">
                    <h4>Passbook per Product</h4>
                    <p>Download individual product passbooks</p>
                  </div>
                  <div className="cv-option-action">
                    <FiDownload />
                  </div>
                </button>
              </div>
            </div>
          </>
        ) : (
          // Product selection
          <>
            <div className="cv-modal-header">
              <div className="cv-header-main">
                <div className="cv-header-title">
                  <div className="cv-header-icon">
                    <FiPackage />
                  </div>
                  <h3>Select Product</h3>
                </div>
                <p>Choose a product to download passbook</p>
              </div>
              <button
                onClick={onClose}
                className="cv-modal-close"
                aria-label="Close"
              >
                <FiX />
              </button>
            </div>

            <div className="cv-modal-body">
              {loading ? (
                <div className="cv-modal-loading">
                  <div className="cv-spinner"></div>
                  <span>Loading products...</span>
                </div>
              ) : (
                <div className="cv-product-list">
                  {products.length === 0 ? (
                    <div className="cv-empty-state">
                      <span style={{ fontSize: '48px', opacity: 0.5 }}>📦</span>
                      <p>No products found</p>
                    </div>
                  ) : (
                    products.map((product, index) => (
                      <button
                        key={index}
                        onClick={() => handleDownloadProduct(product.name)}
                        className="cv-product-item"
                      >
                        <div className="cv-product-icon">
                          <FiPackage />
                        </div>
                        <span>{product.name}</span>
                        <FiDownload className="cv-download-icon" />
                      </button>
                    ))
                  )}
                </div>
              )}

              <button
                onClick={handleBack}
                className="cv-btn cv-btn--secondary"
                style={{ marginTop: '20px', width: '100%' }}
              >
                <FiArrowLeft style={{ marginRight: '8px' }} />
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PassbookDownloadModal;
